import { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

import r1 from "../assets/cover/review/nx1.webp";
import r2 from "../assets/cover/review/nx2.webp";
import r3 from "../assets/cover/review/nx3.webp";
import r4 from "../assets/cover/review/nx4.webp";
import r5 from "../assets/cover/review/nx5.webp";
import r6 from "../assets/cover/review/nx6.webp";
import r7 from "../assets/cover/review/nx7.webp";
import r8 from "../assets/cover/review/nx8.webp";
import r9 from "../assets/cover/review/nx9.webp";
import r10 from "../assets/cover/review/nx10.webp";

const REVIEWS = [
  {
    name: "Jan van de Berg",
    country: "NL",
    img: r1,
    text: "Geweldige service! De beeldkwaliteit is haarscherp (4K) en de Eredivisie-wedstrijden bufferen nooit. Binnen 5 minuten na betaling had ik mijn gegevens via WhatsApp.",
  },
  {
    name: "Sanne De Smet",
    country: "BE",
    img: r2,
    text: "Eindelijk alle Belgische en Nederlandse zenders in één pakket, inclusief alle sportkanalen en de nieuwste films. De helpdesk hielp me direct met installeren op mijn Samsung TV.",
  },
  {
    name: "Mark Vermeulen",
    country: "NL",
    img: r3,
    text: "Al 6 maanden abonnee van het Lifetime-pakket. Geen enkele storing gehad tijdens de Champions League. Echt een aanrader!",
  },
  {
    name: "Anouk Janssens",
    country: "NL",
    img: r4,
    text: "De VOD-sectie is gigantisch. Alles met Nederlandse ondertiteling en prima kwaliteit. Mijn hele gezin is er super blij mee.",
  },
  {
    name: "Kevin Maes",
    country: "BE",
    img: r5,
    text: "Had een klein vraagje over de installatie op Smart IPTV en werd via WhatsApp binnen 2 minuten geholpen. Top service!",
  },
  {
    name: "Dennis Mulder",
    country: "NL",
    img: r6,
    text: "Bespaart me honderden euro's per jaar vergeleken met traditionele providers. Alles werkt vlekkeloos op mijn Firestick.",
  },
  {
    name: "Lotte Bakker",
    country: "NL",
    img: r7,
    text: "Snel geïnstalleerd op de Apple TV en meteen alle Eredivisie-wedstrijden in HD. Geen gedoe, gewoon kijken.",
  },
  {
    name: "Thomas Peeters",
    country: "BE",
    img: r8,
    text: "Beeld en geluid perfect, ook tijdens drukke speelrondes. De prijs-kwaliteitverhouding is uitstekend.",
  },
  {
    name: "Rick de Groot",
    country: "NL",
    img: r9,
    text: "De IBO Pro Player-app draaide meteen na het invoeren van de gegevens. Overzichtelijk menu en alles werkt vloeiend op mijn Android TV-box.",
  },
  {
    name: "Emma Willems",
    country: "BE",
    img: r10,
    text: "Setup was zo gebeurd: playlist toegevoegd en klaar. Live TV, films en series allemaal netjes gesorteerd. Precies wat ik zocht.",
  },
];

export default function Review() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const loop = [...REVIEWS, ...REVIEWS];

  const getStep = () => {
    const el = trackRef.current;
    if (!el) return 1;
    const card = el.querySelector("[data-card]");
    return card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
  };

  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);

  const pause = () => {
    pausedRef.current = true;
  };
  const resumeSoon = (delay = 2000) => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      pausedRef.current = false;
    }, delay);
  };

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    pause();
    el.scrollBy({ left: dir * getStep(), behavior: "smooth" });
    resumeSoon(2500);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    // Slide-by-slide autoplay (één kaart per interval, zoals tv-nexu)
    const advance = () => {
      if (pausedRef.current) return;
      const half = el.scrollWidth / 2;
      // Naadloze lus: als we in de identieke tweede set zitten, spring
      // onzichtbaar terug (zelfde kaart) en schuif daarna verder.
      if (half > 0 && el.scrollLeft >= half - 1) el.scrollLeft -= half;
      el.scrollBy({ left: getStep(), behavior: "smooth" });
    };
    const id = setInterval(advance, 3500);

    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / getStep());
      setActive(((idx % REVIEWS.length) + REVIEWS.length) % REVIEWS.length);
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const onEnter = () => pause();
    const onLeave = () => resumeSoon(500);
    const onTouch = () => {
      pause();
      resumeSoon(4000);
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("touchstart", onTouch, { passive: true });
    el.addEventListener("wheel", onTouch, { passive: true });

    return () => {
      clearInterval(id);
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("touchstart", onTouch);
      el.removeEventListener("wheel", onTouch);
    };
  }, []);

  return (
    <section className="py-12 sm:py-16 bg-slate-50 relative" id="reviews">
      <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto relative">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#090979] rounded-full text-sm font-semibold mb-4 border border-blue-100">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            4.9 / 5 — Beoordeeld door meer dan 12.000 klanten
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-auto font-black mb-2 sm:mb-3 max-w-4xl leading-snug text-center">
            Wat Onze{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
              Klanten Zeggen
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Echte reacties van echte klanten in Nederland en België
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow hover:shadow-md border border-slate-200 text-slate-600 hover:text-[#090979] transition"
            aria-label="Vorige beoordelingen"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow hover:shadow-md border border-slate-200 text-slate-600 hover:text-[#090979] transition"
            aria-label="Volgende beoordelingen"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={trackRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-2 scrollbar-hide"
          >
            {loop.map((review, index) => (
              <div
                key={index}
                data-card
                className="flex-none w-[85%] sm:w-[70%] md:w-1/2 lg:w-1/3 bg-white rounded-xl sm:rounded-2xl shadow-md border border-slate-100 p-4 sm:p-5"
              >
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="overflow-hidden rounded-lg sm:rounded-xl bg-slate-100 h-36 sm:h-44 md:h-56">
                    <img
                      src={review.img}
                      alt={`Beoordeling van ${review.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[#090979] mb-2">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} className="w-4 h-4 fill-[#090979]" />
                      ))}
                    </div>
                    <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                      {review.text}
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                      <span className="inline-flex items-center justify-center flex-shrink-0 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white text-xs font-bold">
                        {review.name.charAt(0)}
                      </span>
                      <div>
                        <p className="text-slate-900 font-semibold text-xs sm:text-sm">
                          {review.name}
                        </p>
                        <p className="text-slate-500 text-xs">{review.country}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {REVIEWS.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition ${
                  active === i ? "bg-slate-800" : "bg-slate-400/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
